// RedFlagBanner — condensed single-line alert bar for critical system flags.
//
// Replaces the previous stack of full-width banners (one per active flag) with
// ONE compact line: count + highest active severity + the newest flag's
// headline, plus Acknowledge-all / View-all / Dismiss-all. Clicking the line
// expands a dropdown listing every flag with its own Acknowledge / View /
// Dismiss. Admin-only and only mounted in the admin chrome (MainLayout).

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminService, type RedFlag } from '../../services/admin';
import { formatRelativeTime } from '../../utils/dateFormatters';
import { WarningIcon, CloseIcon } from '../icons';
import styles from './RedFlagBanner.module.css';

// Session storage key for dismissed flags
const DISMISSED_FLAGS_KEY = 'redFlagBanner_dismissedFlags';
const POLL_INTERVAL_MS = 30000;
// Fetch enough to give an accurate count + complete dropdown without flooding.
const FETCH_LIMIT = 25;

const SEVERITY_RANK: Record<RedFlag['severity'], number> = {
  critical: 3,
  high: 2,
  medium: 1,
};

// Helper to get dismissed flags from session storage
function getDismissedFromSession(): Set<string> {
  try {
    const stored = sessionStorage.getItem(DISMISSED_FLAGS_KEY);
    if (stored) {
      return new Set(JSON.parse(stored));
    }
  } catch {
    // Ignore storage errors
  }
  return new Set();
}

// Helper to save dismissed flags to session storage
function saveDismissedToSession(dismissed: Set<string>): void {
  try {
    sessionStorage.setItem(DISMISSED_FLAGS_KEY, JSON.stringify([...dismissed]));
  } catch {
    // Ignore storage errors
  }
}

function barSeverityClass(severity: RedFlag['severity']): string {
  switch (severity) {
    case 'critical':
      return styles.critical;
    case 'high':
      return styles.high;
    default:
      return styles.medium;
  }
}

function railSeverityClass(severity: RedFlag['severity']): string {
  switch (severity) {
    case 'critical':
      return styles.railCritical;
    case 'high':
      return styles.railHigh;
    default:
      return styles.railMedium;
  }
}

function chipSeverityClass(severity: RedFlag['severity']): string {
  switch (severity) {
    case 'critical':
      return styles.chipCritical;
    case 'high':
      return styles.chipHigh;
    default:
      return styles.chipMedium;
  }
}

export function RedFlagBanner() {
  const { isAuthenticated, user } = useAuth();
  const [flags, setFlags] = useState<RedFlag[]>([]);
  // Initialize dismissed from session storage so dismissals persist across polls/remounts
  const [dismissed, setDismissed] = useState<Set<string>>(() => getDismissedFromSession());
  const [expanded, setExpanded] = useState(false);

  const isAdmin = user?.role === 'ADMIN';

  // Fetch active red flags on mount and poll periodically.
  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return;

    const fetchFlags = async () => {
      try {
        const response = await adminService.getRedFlags({ status: 'active', limit: FETCH_LIMIT });
        setFlags(response.flags);
      } catch (error) {
        // Silently fail - don't break the app if this fails
        console.error('Failed to fetch red flags:', error);
      }
    };

    fetchFlags();
    const interval = setInterval(fetchFlags, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isAuthenticated, isAdmin]);

  // Filter out dismissed/non-active flags, then order by severity (then recency)
  // so the most important, most recent flag leads the bar and colours it.
  const activeFlags = flags
    .filter((flag) => !dismissed.has(flag.id) && flag.status === 'active')
    .sort(
      (a, b) =>
        SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity] ||
        b.created_at.localeCompare(a.created_at),
    );

  const handleDismiss = useCallback((flagId: string) => {
    setDismissed((prev) => {
      const newSet = new Set([...prev, flagId]);
      saveDismissedToSession(newSet);
      return newSet;
    });
  }, []);

  const handleDismissAll = useCallback((flagIds: string[]) => {
    setDismissed((prev) => {
      const newSet = new Set([...prev, ...flagIds]);
      saveDismissedToSession(newSet);
      return newSet;
    });
  }, []);

  // Acknowledge then optimistically drop locally; the next poll reconciles
  // with the server (mirrors CriticalAlertBar's persist-until-acknowledged).
  const handleAcknowledge = useCallback(async (flagId: string) => {
    try {
      await adminService.acknowledgeRedFlag(flagId);
      setFlags((prev) => prev.filter((flag) => flag.id !== flagId));
    } catch (error) {
      console.error('Failed to acknowledge red flag:', error);
    }
  }, []);

  const handleAcknowledgeAll = useCallback(async (flagIds: string[]) => {
    try {
      await Promise.all(flagIds.map((id) => adminService.acknowledgeRedFlag(id)));
      const acked = new Set(flagIds);
      setFlags((prev) => prev.filter((flag) => !acked.has(flag.id)));
    } catch (error) {
      console.error('Failed to acknowledge red flags:', error);
    }
  }, []);

  // Don't render if no flags or not admin
  if (!isAuthenticated || !isAdmin || activeFlags.length === 0) {
    return null;
  }

  const lead = activeFlags[0];
  const count = activeFlags.length;
  const multiple = count > 1;
  const activeIds = activeFlags.map((flag) => flag.id);

  return (
    <div
      className={`${styles.bar} ${barSeverityClass(lead.severity)}`}
      role="alert"
      aria-live="polite"
    >
      <div className={styles.row}>
        <button
          type="button"
          className={styles.summary}
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          aria-label={`${count} active red flag${multiple ? 's' : ''} — toggle details`}
        >
          <WarningIcon className={styles.icon} />
          <span className={styles.count}>
            {count} active flag{multiple ? 's' : ''}
          </span>
          <span className={styles.severity}>{lead.severity.toUpperCase()}</span>
          <span className={styles.lead}>
            <span className={styles.leadTitle}>{lead.title}</span>
            <span className={styles.leadMessage}> — {lead.message}</span>
          </span>
          <svg
            className={`${styles.caret} ${expanded ? styles.caretUp : ''}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.actionButton}
            onClick={() => handleAcknowledgeAll(activeIds)}
          >
            {multiple ? 'Acknowledge all' : 'Acknowledge'}
          </button>
          <Link to="/alerts" className={styles.actionButton}>
            {multiple ? 'View all' : 'View details'}
          </Link>
          <button
            type="button"
            className={styles.dismissButton}
            onClick={() => handleDismissAll(activeIds)}
            title="Dismiss alerts"
            aria-label="Dismiss alerts"
          >
            <CloseIcon className={styles.closeIcon} />
          </button>
        </div>
      </div>

      {expanded && (
        <div className={styles.dropdown}>
          {activeFlags.map((flag) => (
            <div key={flag.id} className={styles.flagRow}>
              <span
                className={`${styles.rail} ${railSeverityClass(flag.severity)}`}
                aria-hidden="true"
              />
              <span className={`${styles.flagChip} ${chipSeverityClass(flag.severity)}`}>
                {flag.severity.toUpperCase()}
              </span>
              <span className={styles.flagTitle}>{flag.title}</span>
              <span className={styles.flagMessage}>{flag.message}</span>
              <span className={styles.flagTime}>{formatRelativeTime(flag.created_at)}</span>
              <span className={styles.flagActions}>
                <button
                  type="button"
                  className={styles.flagButton}
                  onClick={() => handleAcknowledge(flag.id)}
                >
                  Acknowledge
                </button>
                <Link to={`/alerts?id=${flag.id}`} className={styles.flagButton}>
                  View
                </Link>
                <button
                  type="button"
                  className={styles.flagButton}
                  onClick={() => handleDismiss(flag.id)}
                  title="Dismiss"
                  aria-label="Dismiss flag"
                >
                  ✕
                </button>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
